import React, {
  createContext,
  useContext,
  useEffect,
  useState,
  useCallback,
} from "react";
import { useNavigate } from "react-router-dom";
import { jwtDecode } from "jwt-decode";
import { AuthContextType, User, JwtPayload, TenantFeatures } from "../types/auth";
import { MyLocalStorage } from "../utils/storage";

const AuthContext = createContext<AuthContextType | undefined>(undefined);

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
};

interface AuthProviderProps {
  children: React.ReactNode;
}

const isTokenExpired = (token: string): boolean => {
  try {
    const decoded = jwtDecode<JwtPayload>(token);
    const currentTime = Date.now() / 1000;
    return decoded.exp < currentTime;
  } catch {
    return true;
  }
};

const getUserFromToken = (token: string): User | null => {
  try {
    const decoded = jwtDecode<JwtPayload>(token);
    return {
      id: decoded.sub,
      email: decoded.email,
      tenant_id: decoded.tenant_id,
      role: decoded.role,
      is_superadmin: decoded.is_superadmin,
      email_verified: decoded.email_verified ?? false,
    };
  } catch {
    return null;
  }
};

export const getStoredToken = (): string | null => {
  return MyLocalStorage.jwt_token.get();
};

const DEFAULT_FEATURES: TenantFeatures = { 
  serp_preview_enabled: false,
  subscription_plan_name: null,
  subscription_status: null,
};

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [isLoading, setIsLoading] = useState(true);
  const [user, setUser] = useState<User | null>(null);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [tenantFeatures, setTenantFeatures] = useState<TenantFeatures>(DEFAULT_FEATURES);

  const fetchTenantFeatures = useCallback(async () => {
    try {
      const token = getStoredToken();
      if (!token) return;
      const resp = await fetch(`${API_BASE_URL}/api/tenant/current`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (resp.ok) {
        const data = await resp.json();
        setTenantFeatures({
          serp_preview_enabled: data.serp_preview_enabled ?? false,
          subscription_plan_name: data.subscription_plan_name ?? null,
          subscription_status: data.subscription_status ?? null,
        });
      }
    } catch {
      // Silently fail — features default to disabled
    }
  }, []);

  // Initialize auth state from stored token
  useEffect(() => {
    const token = getStoredToken();

    if (token && !isTokenExpired(token)) {
      const userData = getUserFromToken(token);
      if (userData) {
        setUser(userData);
        setIsAuthenticated(true);
      } else {
        MyLocalStorage.jwt_token.remove();
      }
    } else if (token) {
      MyLocalStorage.jwt_token.remove();
    }

    setIsLoading(false);
  }, []);

  useEffect(() => {
    if (isAuthenticated) {
      fetchTenantFeatures();
    } else {
      setTenantFeatures(DEFAULT_FEATURES);
    }
  }, [isAuthenticated, fetchTenantFeatures]);

  const login = useCallback(async (email: string, password: string) => {
    setIsLoading(true);

    try {
      const response = await fetch(`${API_BASE_URL}/api/auth/login`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ email, password }),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.detail || "Invalid email or password");
      }

      const data = await response.json();
      const token = data.access_token;

      // Store token
      MyLocalStorage.jwt_token.set(token);

      // Extract user from token
      const userData = getUserFromToken(token);
      if (userData) {
        setUser(userData);
        setIsAuthenticated(true);
      }
    } finally {
      setIsLoading(false);
    }
  }, []);

  const logout = useCallback(() => {
    MyLocalStorage.jwt_token.remove();
    setUser(null);
    setIsAuthenticated(false);
  }, []);

  /**
   * Login with a pre-obtained token (used for self-service signup flow).
   * This skips the email/password authentication since we already have a valid token.
   */
  const loginWithToken = useCallback((token: string) => {
    if (isTokenExpired(token)) {
      throw new Error("Token is expired");
    }

    // Store token
    MyLocalStorage.jwt_token.set(token);

    // Extract user from token
    const userData = getUserFromToken(token);
    if (userData) {
      setUser(userData);
      setIsAuthenticated(true);
    } else {
      throw new Error("Invalid token");
    }
  }, []);

  const value: AuthContextType = {
    isAuthenticated,
    user,
    tenantFeatures,
    login,
    loginWithToken,
    logout,
    isLoading,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
